import React, { useState, useEffect, useRef } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner'; 
import * as XLSX from 'xlsx';
import { generateA4Slips } from '../../../utils/pdfGenerator'; 

// --- CONFIGURATION CONSTANTS ---
const CANVAS_W = 400;
const CANVAS_H = 300;

// Default Element Layout
const DEFAULT_ELEMENTS = [
  { id: 'header_img', type: 'image', x: 10, y: 5, w: 380, h: 75, src: 'https://placehold.co/380x75/orange/white?text=ELECTION+SYMBOL&font=merriweather' },
  { id: 'separator_line', type: 'line', x: 10, y: 85, w: 380, h: 2, color: '#000000' },
  { id: 'val_panel', type: 'text', x: 10, y: 95, fontSize: 13, isBold: true, text: 'Panel: 12' },
  { id: 'val_booth_num', type: 'text', x: 110, y: 95, fontSize: 13, isBold: true, text: 'Booth: 45' },
  { id: 'val_voter_num', type: 'text', x: 210, y: 95, fontSize: 13, isBold: true, text: 'Voter: 123' },
  { id: 'val_card_no', type: 'text', x: 310, y: 95, fontSize: 13, isBold: true, text: 'GKG1234567' },
  { id: 'val_assembly_info', type: 'text', x: 310, y: 115, fontSize: 11, isBold: false, text: '141/23/890' },
  { id: 'val_name_mr', type: 'text', x: 10, y: 140, fontSize: 14, isBold: true, text: 'नाव: रमेश सुरेश पाटील' },
  { id: 'val_name_en', type: 'text', x: 10, y: 170, fontSize: 12, isBold: true, text: 'Name: Ramesh Suresh Patil' },
  { id: 'val_age_sex', type: 'text', x: 10, y: 195, fontSize: 11, isBold: false, text: 'Age/Sex: 45/Male' },
  { id: 'val_address', type: 'text', x: 10, y: 220, fontSize: 11, isBold: false, text: 'पत्ता: घर नं 101, शिवाजी नगर...' },
  { id: 'val_booth_details', type: 'text', x: 10, y: 250, fontSize: 11, isBold: false, text: 'Booth: Z.P School No 5, Room 2' },
  { id: 'footer_credit', type: 'text', x: 130, y: 280, fontSize: 9, isBold: true, text: 'Issued By: [User ID] - Smart Search', color: '#666666' },
];

const DEFAULT_PAGE_CONFIG = {
    marginTop: 5, marginLeft: 9, marginRight: 12, gapX: 13, gapY: 10, cardsPerPage: 8 
};

// Factory for new layout
const createNewLayout = (name, elements = null, pageConfig = null) => ({
    id: Date.now(),
    layoutName: name || "New Layout",
    printerName: "",
    elements: elements || JSON.parse(JSON.stringify(DEFAULT_ELEMENTS)),
    pageConfig: pageConfig || JSON.parse(JSON.stringify(DEFAULT_PAGE_CONFIG))
});

const SlipSettings = () => {
  // --- STATE ---
  const [layouts, setLayouts] = useState([]); 
  const [activeLayoutIndex, setActiveLayoutIndex] = useState(0);
  const [isDirty, setIsDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(true); 
  const [dataSource, setDataSource] = useState(""); 
  
  // UI State
  const [selectedId, setSelectedId] = useState(null);
  const [viewMode, setViewMode] = useState('slip'); 
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadMeta, setUploadMeta] = useState({ name: '', printerName: '' });

  // Mouse / Dragging
  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });


  // --- NEW: Excel Data State ---
  const [importedData, setImportedData] = useState([]);
  const fileInputRef = useRef(null);


  // Helpers to get current active data safely
  const currentLayout = layouts[activeLayoutIndex] || {};
  const elements = currentLayout.elements || []; 
  const pageConfig = currentLayout.pageConfig || DEFAULT_PAGE_CONFIG;

  // --- 1. INITIAL LOAD (Direct POST -> Proxy Fallback -> Local Storage) ---
  const initializeData = async () => {
    setIsLoading(true);
    let loadedData = null;
    let source = "";

    const targetUrl = 'http://anuplights.in/uploads/json/slip_settings.json';

    // A. ATTEMPT 1: DIRECT SERVER FETCH (POST)
    try {
        console.log("[-] Attempt 1: Fetching Direct (POST)...");
        const response = await fetch(targetUrl + '?t=' + Date.now(), { 
            method: 'POST', 
            cache: "no-store" 
        });

        if (response.ok) {
            const json = await response.json();
            if (Array.isArray(json) && json.length > 0) {
                loadedData = json;
                source = "SERVER (DIRECT)";
                console.log("[-] SUCCESS: Loaded via Direct POST");
            }
        } else {
            throw new Error(`Direct POST failed with status: ${response.status}`);
        }
    } catch (directError) {
        console.warn(`[-] Direct fetch failed (${directError.message}). Attempting Proxy...`);

        // B. ATTEMPT 2: PROXY FETCH (GET)
        try {
            // 🔥 Proxy URL wrapping the target URL
            const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(targetUrl)}&t=${Date.now()}`;
            
            const response = await fetch(proxyUrl);
            
            if (response.ok) {
                const json = await response.json();
                if (Array.isArray(json) && json.length > 0) {
                    loadedData = json;
                    source = "SERVER (PROXY)";
                    console.log("[-] SUCCESS: Loaded via Proxy");
                }
            }
        } catch (proxyError) {
            console.warn("[-] Proxy fetch failed:", proxyError);
        }
    }

    // C. ATTEMPT 3: LOCAL STORAGE FALLBACK
    if (!loadedData) {
        const localMaster = localStorage.getItem("ALL_SLIP_LAYOUTS");
        if (localMaster) {
            try { 
                const parsed = JSON.parse(localMaster); 
                if (Array.isArray(parsed) && parsed.length > 0) {
                    loadedData = parsed;
                    source = "LOCAL";
                    console.log("[-] Loaded layouts from Local Storage");
                }
            } catch(e){}
        }
    }

    // D. ATTEMPT 4: LEGACY KEYS (Migration)
    if (!loadedData) {
        const singleLegacy = localStorage.getItem("SLIP_TEMPLATE_CONFIG");
        const singlePage = localStorage.getItem("SLIP_PAGE_CONFIG");
        if (singleLegacy) {
            try {
                const parsedEl = JSON.parse(singleLegacy);
                const parsedPage = singlePage ? JSON.parse(singlePage) : null;
                loadedData = [createNewLayout("Local Layout", parsedEl, parsedPage)];
                source = "LEGACY";
            } catch(e){}
        }
    }

    // E. FINAL SAFETY NET (Default)
    if (!loadedData || !Array.isArray(loadedData) || loadedData.length === 0) {
        console.log("[-] No data found anywhere. Creating Default.");
        loadedData = [createNewLayout("Default Layout")];
        source = "DEFAULT";
    }

    // F. FILTER & APPLY STATE
    // Check strict array and filter empty items
    const validLayouts = Array.isArray(loadedData) 
        ? loadedData.filter(l => l && l.layoutName && l.layoutName.trim() !== "")
        : [];

    // Fallback if filter cleared everything
    if(validLayouts.length === 0) validLayouts.push(createNewLayout("Default Layout"));

    setLayouts(validLayouts);
    setActiveLayoutIndex(0);
    setDataSource(source);
    setIsDirty(false);

    // Sync Active Data for PDF Generator
    const first = validLayouts[0];
    localStorage.setItem("SLIP_TEMPLATE_CONFIG", JSON.stringify(first.elements));
    localStorage.setItem("SLIP_PAGE_CONFIG", JSON.stringify(first.pageConfig));

    // Update Local Storage if we got fresh data from server
    if (source.includes("SERVER")) {
        localStorage.setItem("ALL_SLIP_LAYOUTS", JSON.stringify(validLayouts));
    }

    setIsLoading(false);
  };

  useEffect(() => {
    initializeData();
  }, []);

  // --- 2. UPDATE HELPERS ---
  const updateActiveLayout = (key, value) => {
      const updatedLayouts = [...layouts];
      if (!updatedLayouts[activeLayoutIndex]) return;

      updatedLayouts[activeLayoutIndex] = {
          ...updatedLayouts[activeLayoutIndex],
          [key]: value
      };
      setLayouts(updatedLayouts);
      setIsDirty(true); 
  };

  const updateElement = (id, updates) => {
    const newElements = elements.map(el => (el.id === id ? { ...el, ...updates } : el));
    updateActiveLayout('elements', newElements);
  };

  const updatePageConfig = (key, value) => {
    const newConfig = { ...pageConfig, [key]: parseInt(value) || 0 };
    updateActiveLayout('pageConfig', newConfig);
  };

  // --- 3. UI ACTIONS ---
  const handleLayoutChange = (e) => {
      if (isDirty && !window.confirm("Unsaved changes! Switch anyway?")) return;
      const newIndex = parseInt(e.target.value);
      setActiveLayoutIndex(newIndex);
      setSelectedId(null);
      setIsDirty(false); 

      // Sync for PDF
      const newLayout = layouts[newIndex];
      if (newLayout) {
        localStorage.setItem("SLIP_TEMPLATE_CONFIG", JSON.stringify(newLayout.elements));
        localStorage.setItem("SLIP_PAGE_CONFIG", JSON.stringify(newLayout.pageConfig));
      }
  };

  const handleAddNewLayout = () => {
      const name = prompt("Enter Name for New Layout:");
      if (!name) return;
      const newLayout = createNewLayout(name);
      const newLayouts = [...layouts, newLayout];
      setLayouts(newLayouts);
      setActiveLayoutIndex(newLayouts.length - 1);
      setIsDirty(true);
  };

  const handleDeleteLayout = () => {
      if (layouts.length <= 1) {
          alert("Cannot delete the only remaining layout.");
          return;
      }
      if (!window.confirm(`Delete "${currentLayout.layoutName}"?`)) return;

      const updatedLayouts = layouts.filter((_, i) => i !== activeLayoutIndex);
      setLayouts(updatedLayouts);
      setActiveLayoutIndex(0); 
      setIsDirty(true); 
  };

  const handleResetLayout = () => {
      if (!window.confirm("Reset this layout to defaults?")) return;
      const updatedLayouts = [...layouts];
      updatedLayouts[activeLayoutIndex] = {
          ...updatedLayouts[activeLayoutIndex],
          elements: JSON.parse(JSON.stringify(DEFAULT_ELEMENTS)),
          pageConfig: JSON.parse(JSON.stringify(DEFAULT_PAGE_CONFIG))
      };
      setLayouts(updatedLayouts);
      setIsDirty(true);
      setSelectedId(null);
  };

 
  const handleSetData = () => {
   
    // 🔥 MODIFIED: Saving as OBJECT (Removed Array brackets)
    const strictFormatData = {
        "id": currentLayout.id || Date.now(),
        "layoutName": currentLayout.layoutName || "Default Layout",
        "printerName": currentLayout.printerName || "", // Ensures string field exists
        "elements": elements,      // Your current elements state
        "pageConfig": pageConfig   // Your current pageConfig state
    };

    localStorage.setItem("CACHED_LAYOUT_DATA", JSON.stringify(strictFormatData));

    setIsDirty(false); 
    alert("Data Saved Locally in Strict Format!"); // Changed console.log to alert for visibility
  };

  const openUploadModal = () => {
      setUploadMeta({
          name: currentLayout.layoutName || '',
          printerName: currentLayout.printerName || ''
      });
      setShowUploadModal(true);
  };

  const performUpload = async () => {
    if (!uploadMeta.name || !uploadMeta.printerName) {
        alert("Please fill in Name and Printer Name.");
        return;
    }

    const updatedLayouts = [...layouts];
    updatedLayouts[activeLayoutIndex] = {
        ...updatedLayouts[activeLayoutIndex],
        layoutName: uploadMeta.name,
        printerName: uploadMeta.printerName
    };
    
    setLayouts(updatedLayouts);
    
    // Save Locally First
    localStorage.setItem("ALL_SLIP_LAYOUTS", JSON.stringify(updatedLayouts));
    localStorage.setItem("SLIP_TEMPLATE_CONFIG", JSON.stringify(updatedLayouts[activeLayoutIndex].elements));
    localStorage.setItem("SLIP_PAGE_CONFIG", JSON.stringify(updatedLayouts[activeLayoutIndex].pageConfig));
    setIsDirty(false);

    const fileName = 'slip_settings.json';
    const uploadUrl = 'https://smartsearch.anuplights.com/upload';
    
    const jsonString = JSON.stringify(updatedLayouts, null, 4);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const file = new File([blob], fileName, { type: 'application/json' });

    const formData = new FormData();
    formData.append('file', file);

    try {
        const response = await fetch(uploadUrl, { method: 'POST', body: formData });
        if (response.ok) {
            alert(`Uploaded Successfully!`);
            setShowUploadModal(false);
        } else {
            alert(`Upload Failed: ${response.statusText}`);
        }
    } catch (error) {
        alert('Network Error during upload.');
    }
  };

  // --- 5. EXCEL IMPORT & PDF EXPORT LOGIC ---
  const handleImportExcel = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws);

        if (!data || data.length === 0) {
            alert("No data found in Excel sheet.");
            return;
        }

        // --- Data Parsing & Name Splitting Logic ---
        const formattedData = data.map((row) => {
            const rawFullName = row['fullname'] || "";
            // Divide name into sections by space
            const parts = rawFullName.trim().split(/\s+/);
            
            let surname = "";
            let restOfName = "";

            if (parts.length > 0) {
                // Last part is the surname
                surname = parts[parts.length - 1];
                // Everything before the last part is the name (First + Middle)
                restOfName = parts.slice(0, parts.length - 1).join(" ");
            }

            // Map keys exactly as required by generateA4Slips
            return {
                // 🔴 UPDATED MAPPINGS FOR TOP SECTION 🔴
                PanelNumber: row['Prabhag No '] ? String(row['Prabhag No ']) : "", 
                BoothNumber: row['Booth I'] ? String(row['Booth I']) : (row['Booth No'] ? String(row['Booth No']) : ""), // Checks "Booth I" first, then "Booth No"
                VoterNumber: row['Sr No.'] ? String(row['Sr No.']) : "",
                CardNo: row['card no'] || "",
                
                // Name Data divided into 3 sections logic:
                esurname: surname, 
                name_english: restOfName,
                surname_marathi: surname,
                name_marathi: restOfName,
                
                age: row['age'],
                sex: row['sex'] === 'M' ? 'Male' : (row['sex'] === 'F' ? 'Female' : row['sex']),
                address: row['address'],
                BoothAddress1: row['Booth Name'], // This maps to the Bottom Booth Details
                raw: row
            };
        });

        setImportedData(formattedData);
        alert(`Successfully imported ${formattedData.length} records! Ready to Export PDF.`);
        if(fileInputRef.current) fileInputRef.current.value = "";
    };
    reader.readAsBinaryString(file);
  };

const handleExportPDF = () => {
      if (!importedData || importedData.length === 0) {
          alert("No Excel data imported yet. Please Import Excel first.");
          return;
      }
      
      // 🔥 MODIFIED: Saving as OBJECT (Removed Array brackets)
      const strictFormatData = {
        "id": currentLayout.id || Date.now(),
        "layoutName": currentLayout.layoutName || "Local Export",
        "printerName": currentLayout.printerName || "", 
        "elements": elements,
        "pageConfig": pageConfig
      };

      // Save to the specific key your generator depends on
      localStorage.setItem("CACHED_LAYOUT_DATA", JSON.stringify(strictFormatData));

      generateA4Slips({
          data: importedData,
          fileName: "Imported_Voter_Slips.pdf",
          slipImageUrl: elements.find(e => e.id === 'header_img')?.src,
          candidateName: "Candidate",
          allowLogoPrint: true
      });
  };
  const handleDemoPrintWrapper = () => {
      localStorage.setItem("SLIP_TEMPLATE_CONFIG", JSON.stringify(elements));
      localStorage.setItem("SLIP_PAGE_CONFIG", JSON.stringify(pageConfig));

      const count = pageConfig.cardsPerPage || 8;
      const dummyData = Array.from({ length: count }).map((_, i) => ({
          PanelNumber: "12", VoterNumber: (100 + i).toString(), CardNo: `DEMO${1000+i}`,
          esurname: "Patil", name_english: "Ramesh Suresh",
          surname_marathi: "पाटील", name_marathi: "रमेश सुरेश",
          age: 35 + i, sex: i % 2 === 0 ? "Male" : "Female",
          address: "Flat 101, Demo Building, Main Road", BoothAddress1: "Z.P School No 5",
          raw: {} 
      }));

      generateA4Slips({
          data: dummyData,
          fileName: "Demo_Print_Preview.pdf",
          slipImageUrl: elements.find(e => e.id === 'header_img')?.src, 
          candidateName: "Demo",
          allowLogoPrint: true
      });
  };

  // --- MOUSE HANDLERS ---
  const handleMouseDown = (e, id) => {
    if (viewMode !== 'slip') return;
    e.stopPropagation(); e.preventDefault();
    setSelectedId(id);
    const el = elements.find(item => item.id === id);
    if (!el) return;
    setDragOffset({ x: e.clientX - el.x, y: e.clientY - el.y });
    setIsDragging(true);
  };

  const handleMouseMove = (e) => {
    if (viewMode !== 'slip' || !isDragging || !selectedId) return;
    let newX = e.clientX - dragOffset.x;
    let newY = e.clientY - dragOffset.y;
    newX = Math.max(-50, Math.min(newX, CANVAS_W + 10)); 
    newY = Math.max(-50, Math.min(newY, CANVAS_H + 10));
    updateElement(selectedId, { x: newX, y: newY });
  };
  const handleMouseUp = () => setIsDragging(false);
  const selectedElement = elements.find(el => el.id === selectedId);

  // --- 🔥 LOADING SCREEN ---
  if (isLoading) {
      return (
          <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh', background: '#f8fafc' }}>
              <div className="text-center">
                  <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
                  <h5 className="mt-3 text-secondary">Loading Layouts...</h5>
                  <small className="text-muted">Connecting to Server...</small>
              </div>
          </div>
      );
  }

  return (
    <div className="d-flex w-100" style={{ height: '100vh', overflow: 'hidden', fontFamily: '"Segoe UI", Roboto, sans-serif' }}
         onMouseMove={handleMouseMove} onMouseUp={handleMouseUp}>
      
{/* Hidden File Input for Excel */}
      <input 
        type="file" 
        accept=".xlsx, .xls" 
        ref={fileInputRef} 
        style={{display: 'none'}} 
        onChange={handleImportExcel} 
      />

      {/* CANVAS AREA */}
      <div style={{ flex: 1, background: '#f8fafc', display: 'flex', flexDirection: 'column', position: 'relative' }}>
        <div className="p-3 d-flex justify-content-between align-items-center" style={{ position: 'absolute', top: 0, left: 0, width: '100%', zIndex: 100 }}>
            <div className="d-flex gap-2">
                <Button variant="white" className="shadow-sm border bg-white" onClick={() => window.history.back()}>Back</Button>
                <Button variant="dark" className="shadow-sm border" onClick={handleDemoPrintWrapper}>🖨️ Demo Print</Button>
            </div>
            {/* NEW BUTTONS EXACTLY BELOW DEMO PRINT */}
<div className="d-flex gap-2 mt-2">
    <Button 
        variant="light" 
        className="shadow-sm border bg-white d-flex align-items-center gap-2" 
        onClick={() => fileInputRef.current.click()}
        title="Import Excel File"
    >
            <span style={{ color: 'green', fontWeight: 'bold' }}>✖</span> Import Excel
    </Button>
    <Button 
        variant="light" 
        className="shadow-sm border bg-white d-flex align-items-center gap-2" 
        onClick={handleExportPDF}
        disabled={importedData.length === 0}
        title={importedData.length === 0 ? "Import Data First" : "Export to PDF"}
    >
        <span style={{ color: 'red', fontWeight: 'bold' }}>⬇</span> Export PDF
    </Button>
</div>
             <div className='d-flex gap-2'>
                {/* 🔥 REFRESH BUTTON FOR DEBUGGING */}
                <Button variant="outline-primary" size="sm" className="shadow-sm" onClick={initializeData} title="Force Refresh">↻ Refresh</Button>
                <Button variant="danger" size="sm" className="shadow-sm" onClick={handleResetLayout}>Reset Layout</Button>
             </div>
        </div>

        <div className="d-flex flex-column align-items-center justify-content-center flex-grow-1" style={{ overflow: 'auto', paddingTop: '60px' }}>
            <h4 className="mb-4 text-secondary fw-bold">
                {viewMode === 'slip' ? `Editing: ${currentLayout.layoutName || 'Layout'}` : "Page Layout Preview"}
            </h4>
            
            {viewMode === 'slip' ? (
                <div ref={containerRef} onClick={() => setSelectedId(null)} 
                     style={{ width: `${CANVAS_W}px`, height: `${CANVAS_H}px`, background: 'white', border: '1px solid #94a3b8', position: 'relative', boxShadow: '0 20px 40px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                {elements.map(el => (
                    <div key={el.id} onMouseDown={(e) => handleMouseDown(e, el.id)} onClick={(e) => e.stopPropagation()}
                    style={{
                        position: 'absolute', left: el.x, top: el.y, 
                        width: el.w || 'auto', 
                        height: el.type === 'line' ? '14px' : (el.h || 'auto'), 
                        border: selectedId === el.id ? '2px dashed #3b82f6' : '1px solid transparent',
                        zIndex: selectedId === el.id ? 100 : (el.type === 'image' ? 1 : 10), 
                        cursor: isDragging && selectedId === el.id ? 'grabbing' : 'grab',
                        ...(el.type === 'line' ? { display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.05)' } : {}),
                        ...(el.type === 'text' ? { fontSize: `${el.fontSize}px`, fontWeight: el.isBold ? 'bold' : 'normal', color: el.color, whiteSpace: 'nowrap' } : {})
                    }}>
                    {el.type === 'image' && <img src={el.src} style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }} alt="" />}
                    {el.type === 'text' && el.text}
                    {el.type === 'line' && <div style={{ width: '100%', height: `${el.h || 1}px`, backgroundColor: el.color || '#000' }}></div>}
                    </div>
                ))}
                </div>
            ) : (
                // --- PAGE GRID PREVIEW ---
                <div style={{ 
                    width: '320px', height: '452px', background: 'white', border: '1px solid #ccc', boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                    display: 'grid', 
                    padding: `${pageConfig.marginTop/3}px ${pageConfig.marginRight/3}px ${pageConfig.marginTop/3}px ${pageConfig.marginLeft/3}px`, 
                    gridTemplateColumns: '1fr 1fr', gap: `${pageConfig.gapY/3}px ${pageConfig.gapX/3}px`,
                    alignContent: 'start', overflow: 'hidden'
                }}>
                    {Array.from({length: pageConfig.cardsPerPage}).map((_, i) => (
                        <div key={i} style={{ border: '1px solid #ddd', background: '#fff', position: 'relative', overflow: 'hidden', height: '100%', minHeight: '80px' }}>
                             <div className="d-flex align-items-center justify-content-center h-100 text-muted small">Card {i+1}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
      </div>

      {/* PROPERTIES SIDEBAR */}
      <div className="shadow-lg bg-white d-flex flex-column" style={{ width: '340px' }}>
        
        {/* LAYOUT SELECTOR */}
        <div className="p-3 bg-light border-bottom">
            <label className="small fw-bold text-muted mb-1">Select Layout ({dataSource})</label>
            <div className="d-flex gap-2">
                <Form.Select value={activeLayoutIndex} onChange={handleLayoutChange} size="sm">
                    {layouts.map((l, idx) => {
                        if (!l || !l.layoutName) return null;
                        return (
                            <option key={l.id || idx} value={idx}>
                                {l.layoutName} {l.printerName ? `- [${l.printerName}]` : ''} {isDirty && idx === activeLayoutIndex ? '*' : ''}
                            </option>
                        );
                    })}
                </Form.Select>
                <Button variant="outline-danger" size="sm" onClick={handleDeleteLayout}>🗑️</Button>
                <Button variant="outline-success" size="sm" onClick={handleAddNewLayout}>+</Button>
            </div>
            <div className="small text-muted mt-2" style={{ fontSize: '12px' }}>
                🖨️ Printer: <strong>{currentLayout.printerName || "Not Set"}</strong>
            </div>
        </div>

        <div className="p-2 bg-light border-bottom fw-bold text-secondary small">PROPERTIES</div>
        
        <div style={{ flex: 1, padding: '20px', overflowY: 'auto' }}>
            {selectedElement && viewMode === 'slip' ? (
                 <Form>
                    <div className="mb-3 p-2 bg-light border rounded text-primary fw-bold small d-flex justify-content-between">
                        <span>{selectedElement.id}</span>
                        <span className="text-muted cursor-pointer" onClick={() => setSelectedId(null)} style={{cursor:'pointer'}}>✕</span>
                    </div>

                    {selectedElement.type === 'text' && (
                        <>
                        <Form.Control className="mb-2" value={selectedElement.text} onChange={e => updateElement(selectedElement.id, {text: e.target.value})} />
                        <div className="row g-2 mb-2">
                             <div className="col-6"><Form.Control type="number" size="sm" placeholder="Size" value={selectedElement.fontSize} onChange={e => updateElement(selectedElement.id, {fontSize: parseInt(e.target.value)||0})} /></div>
                             <div className="col-6"><Form.Check type="switch" label="Bold" checked={selectedElement.isBold} onChange={e => updateElement(selectedElement.id, {isBold: e.target.checked})} /></div>
                        </div>
                        </>
                    )}
                    <div className="row g-2">
                        <div className="col-6"><label className="small">X</label><Form.Control size="sm" type="number" value={Math.round(selectedElement.x)} onChange={(e) => updateElement(selectedElement.id, { x: parseInt(e.target.value)||0 })} /></div>
                        <div className="col-6"><label className="small">Y</label><Form.Control size="sm" type="number" value={Math.round(selectedElement.y)} onChange={(e) => updateElement(selectedElement.id, { y: parseInt(e.target.value)||0 })} /></div>
                        {(selectedElement.type === 'image' || selectedElement.type === 'line') && (
                            <div className="col-12"><label className="small">Width</label><Form.Control size="sm" type="number" value={selectedElement.w} onChange={(e) => updateElement(selectedElement.id, { w: parseInt(e.target.value)||0 })} /></div>
                        )}
                    </div>
                 </Form>
            ) : (
                // --- PAGE PROPERTIES ---
                <div>
                    <div className="text-center text-muted mb-4 small fst-italic">
                        {viewMode === 'slip' ? "Select an element to edit specific items." : "Previewing Page Layout"}
                    </div>
                    
                    <h6 className="fw-bold text-secondary border-bottom pb-2 mb-3" style={{fontSize: '12px'}}>PAGE SETTINGS (Current Layout)</h6>
                    
                    <div className="mb-3">
                        <label className="form-label small fw-bold text-muted">Cards Per Page (A4)</label>
                        <Form.Select value={pageConfig.cardsPerPage} onChange={(e) => updatePageConfig('cardsPerPage', e.target.value)}>
                            <option value="4">4 Cards (2x2) - Large</option>
                            <option value="6">6 Cards (2x3) - Medium</option>
                            <option value="8">8 Cards (2x4) - Standard</option>
                            <option value="10">10 Cards (2x5) - Compact</option>
                        </Form.Select>
                    </div>

                    <h6 className="fw-bold text-secondary border-bottom pb-2 mb-3 mt-4" style={{fontSize: '12px'}}>MARGINS & GAPS (mm)</h6>
                    <div className="row g-3">
                        <div className="col-6"><label className="form-label small fw-bold text-muted">Top Margin</label><Form.Control type="number" value={pageConfig.marginTop} onChange={(e) => updatePageConfig('marginTop', e.target.value)} /></div>
                        <div className="col-6"><label className="form-label small fw-bold text-muted">Left Margin</label><Form.Control type="number" value={pageConfig.marginLeft} onChange={(e) => updatePageConfig('marginLeft', e.target.value)} /></div>
                        <div className="col-6"><label className="form-label small fw-bold text-muted">Gap Horizontal</label><Form.Control type="number" value={pageConfig.gapX} onChange={(e) => updatePageConfig('gapX', e.target.value)} /></div>
                        <div className="col-6"><label className="form-label small fw-bold text-muted">Gap Vertical</label><Form.Control type="number" value={pageConfig.gapY} onChange={(e) => updatePageConfig('gapY', e.target.value)} /></div>
                    </div>
                </div>
            )}
        </div>
        
        {/* ACTION BUTTONS */}
        <div className="p-3 border-top d-flex gap-2 flex-column">
            <Button 
                variant={isDirty ? "danger" : "success"} 
                className="w-100 fw-bold" 
                onClick={handleSetData}
            >
                {isDirty ? "● Set Data (Unsaved)" : "✔ Data Synced (Ready)"}
            </Button>
            <Button variant="primary" className="w-100" onClick={openUploadModal}>
                ☁ Upload Data
            </Button>
        </div>
      </div>

      {/* UPLOAD MODAL */}
      <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Upload Configuration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>User ID :</Form.Label>
              <Form.Control type="text" value={uploadMeta.name} onChange={(e) => setUploadMeta({...uploadMeta, name: e.target.value})} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Printer Name</Form.Label>
              <Form.Control type="text" value={uploadMeta.printerName} onChange={(e) => setUploadMeta({...uploadMeta, printerName: e.target.value})} />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUploadModal(false)}>Close</Button>
          <Button variant="primary" onClick={performUpload}>Save & Upload</Button>
        </Modal.Footer>
      </Modal>

      {/* VIEW TOGGLE */}
      <div style={{ position: 'fixed', bottom: '30px', left: '50%', transform: 'translateX(-50%)', zIndex: 1000 }}>
          <div style={{ background: '#333', padding: '5px', borderRadius: '30px', display: 'flex', gap: '5px' }}>
              <button onClick={() => setViewMode('slip')} style={{ padding: '8px 20px', borderRadius: '25px', border: 'none', background: viewMode === 'slip' ? 'white' : 'transparent', color: viewMode === 'slip' ? 'black' : '#ccc'}}>✏️ Slip View</button>
              <button onClick={() => setViewMode('page')} style={{ padding: '8px 20px', borderRadius: '25px', border: 'none', background: viewMode === 'page' ? 'white' : 'transparent', color: viewMode === 'page' ? 'black' : '#ccc'}}>📄 Page View</button>
          </div>
      </div>

    </div>
  );
};
export default SlipSettings;